import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, InputNumber, message, Popconfirm, Segmented, Select, Slider, Spin, Table, Tag, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useProjectStore } from '../../hooks/useProjectStore';
import {
  getApiErrorMessage,
  imageLogAPI,
  type ImageLogBoreholeFeature,
  type ImageLogFracture,
  type ImageLogImages,
  type ImageLogListItem,
  type ImageLogParams,
  type ImageLogPickStatus,
  type ImageLogRecord,
} from '../../services/api';
import { useAgentCommands } from '../../services/agent/registry';
import { useT } from '../../i18n';

// ═══════════════════════════════════════════════
// 成像测井解释 (井壁稳定)
//
// 上传 PDF / 图片 → 后端 OpenCV 增强、二值化、正弦拾取 → 这里叠加显示候选,
// 人工逐条确认/否决/改类型 → 发布到天然裂缝表。崩落与诱导缝给出最大水平主应力方位,
// 用于校核地应力方向与坍塌压力。所有自动结果都是"候选", 不确认不发布。
// ═══════════════════════════════════════════════

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', soft: '#ECEEF1', panel: '#F4F6F9', accent: '#C2533A',
};

const STATUS_COLOR: Record<ImageLogPickStatus, string> = {
  candidate: '#F5A623', accepted: '#27AE60', rejected: '#9AA5B1',
};

const FRACTURE_TYPES: Array<{ value: ImageLogFracture['type']; label: string }> = [
  { value: 'open', label: '张开缝' },
  { value: 'filled', label: '充填缝' },
  { value: 'healed', label: '闭合缝' },
  { value: 'induced', label: '诱导缝' },
  { value: 'bedding', label: '层理缝' },
];

const DEFAULT_PARAMS: ImageLogParams = {
  top_depth: 0, bottom_depth: 0, diameter_mm: 215.9, start_azimuth: 0, dark_features: true,
  crop_left: 0, crop_right: 1, crop_top: 0, crop_bottom: 1, min_coverage: 0.5, max_dip: 80, page: 0,
};

const VIEW_WIDTH = 540;
type Layer = keyof ImageLogImages;

const niceStep = (range: number) => {
  const raw = range / 12;
  const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1e-6)));
  return [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? pow * 10;
};

export default function ImageLogPage() {
  const t = useT();
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [records, setRecords] = useState<ImageLogListItem[]>([]);
  const [record, setRecord] = useState<ImageLogRecord | null>(null);
  const [images, setImages] = useState<ImageLogImages | null>(null);
  const [layer, setLayer] = useState<Layer>('work');
  const [zoom, setZoom] = useState(0.5);
  const [params, setParams] = useState<ImageLogParams>(DEFAULT_PARAMS);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  const loadList = () => {
    if (!projectId) return;
    imageLogAPI.list(projectId).then(({ data }) => setRecords(Array.isArray(data) ? data : [])).catch(() => setRecords([]));
  };
  useEffect(() => { loadList(); setRecord(null); setImages(null); }, [projectId]);

  const applyRecord = (next: ImageLogRecord) => {
    setRecord(next);
    setParams({ ...DEFAULT_PARAMS, ...next.params });
    if (next.images) setImages(next.images);
  };

  const openRecord = async (id: number) => {
    setBusy(true);
    try {
      const { data } = await imageLogAPI.get(id);
      applyRecord(data);
      setImages(null);
      setSelected(null);
      const { data: imgs } = await imageLogAPI.images(id);
      setImages(imgs);
    } catch (error) {
      message.error(getApiErrorMessage(error, t('读取识别记录失败')));
    } finally {
      setBusy(false);
    }
  };

  const validateParams = () => {
    if (!(params.bottom_depth > params.top_depth)) {
      message.warning(t('请填写图像顶深和底深, 底深须大于顶深'));
      return false;
    }
    return true;
  };

  const runUpload = async () => {
    if (!projectId || !file) { message.warning(t('请先选择成像测井 PDF 或图片')); return false; }
    if (!validateParams()) return false;
    setBusy(true);
    try {
      const { data } = await imageLogAPI.upload(projectId, file, params);
      applyRecord(data);
      setSelected(null);
      setFile(null);
      loadList();
      message.success(t('识别完成: {n} 条裂缝候选, {m} 段崩落/诱导缝', { n: data.fractures.length, m: data.borehole_features.length }));
      return true;
    } catch (error) {
      message.error(getApiErrorMessage(error, t('识别失败')));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runReanalyze = async () => {
    if (!record || !validateParams()) return false;
    setBusy(true);
    try {
      const { data } = await imageLogAPI.reanalyze(record.id, params);
      applyRecord(data);
      setSelected(null);
      loadList();
      message.success(t('已按新参数重新识别'));
      return true;
    } catch (error) {
      message.error(getApiErrorMessage(error, t('识别失败')));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const review = async (data: Parameters<typeof imageLogAPI.review>[1]) => {
    if (!record) return;
    try {
      const { data: next } = await imageLogAPI.review(record.id, data);
      setRecord((current) => (current ? { ...next, images: current.images } : next));
      loadList();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('保存复核结果失败')));
    }
  };

  const setFractureStatus = (ids: number[], status: ImageLogPickStatus) =>
    review({ fractures: ids.map((id) => ({ id, status })) });

  const publish = async () => {
    if (!record) return false;
    try {
      const { data } = await imageLogAPI.publish(record.id);
      message.success(t('已发布 {n} 条裂缝到「天然裂缝 · 裂缝参数」', { n: data.published }));
      const { data: next } = await imageLogAPI.get(record.id);
      setRecord((current) => ({ ...next, images: current?.images }));
      loadList();
      return true;
    } catch (error) {
      message.error(getApiErrorMessage(error, t('发布失败')));
      return false;
    }
  };

  const remove = async (id: number) => {
    try {
      await imageLogAPI.remove(id);
      if (record?.id === id) { setRecord(null); setImages(null); }
      loadList();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('删除失败')));
    }
  };

  const acceptedCount = record?.fractures.filter((f) => f.status === 'accepted').length ?? 0;

  // 爱压 AI 操作模式
  useAgentCommands({
    'imaging.analyze': async () => {
      if (!(await (record && !file ? runReanalyze() : runUpload()))) throw new Error('识别未完成, 原因见页面提示');
      return '识别完成, 请在右侧逐条复核';
    },
    'imaging.publish': async () => {
      if (!record) throw new Error('请先打开或上传一份成像测井');
      if (!acceptedCount) throw new Error('还没有已确认的裂缝');
      if (!(await publish())) throw new Error('发布失败');
      return `已发布 ${acceptedCount} 条裂缝`;
    },
  }, () => (record
    ? `成像测井解释: ${record.name}, 深度 ${record.params.top_depth}–${record.params.bottom_depth} m, `
      + `裂缝候选 ${record.fractures.length} 条 (已确认 ${acceptedCount}), 崩落/诱导缝 ${record.borehole_features.length} 段, `
      + `SHmax ${record.summary.shmax_azimuth ?? '—'}°`
    : '成像测井解释: 尚未打开识别记录'));

  // ── 叠加层几何 ──
  const geometry = useMemo(() => {
    if (!record || !record.work_width) return null;
    const scaleX = VIEW_WIDTH / record.work_width;
    const scaleY = scaleX * zoom * 2;
    const height = record.work_height * scaleY;
    const { top_depth: top, bottom_depth: bottom } = record.params;
    const depthToY = (d: number) => ((d - top) / (bottom - top)) * height;
    const curve = (f: ImageLogFracture) => {
      const pts: string[] = [];
      for (let x = 0; x <= record.work_width; x += 2) {
        const y = f.y0_px + f.amplitude_px * Math.sin((2 * Math.PI * x) / record.work_width + f.phase);
        pts.push(`${(x * scaleX).toFixed(1)},${(y * scaleY).toFixed(1)}`);
      }
      return pts.join(' ');
    };
    const step = niceStep(bottom - top);
    const ticks: number[] = [];
    for (let d = Math.ceil(top / step) * step; d <= bottom + 1e-9; d += step) ticks.push(Number(d.toFixed(6)));
    return { scaleX, scaleY, height, depthToY, curve, ticks };
  }, [record, zoom]);

  const focus = (id: number, y: number) => {
    setSelected(id);
    if (viewerRef.current && geometry) viewerRef.current.scrollTop = Math.max(0, y * geometry.scaleY - 120);
  };

  if (!currentProject) return <div style={{ padding: 40, color: TH.textSec }}>{t('请先选择项目')}</div>;

  const numberField = (label: string, key: keyof ImageLogParams, props: { min?: number; max?: number; step?: number } = {}) => (
    <label style={fieldStyle}>
      <span>{t(label)}</span>
      <InputNumber
        size="small"
        style={{ width: 110 }}
        value={params[key] as number}
        onChange={(v) => setParams((p) => ({ ...p, [key]: Number(v ?? 0) }))}
        {...props}
      />
    </label>
  );

  const fractureColumns = [
    { title: t('深度 (m)'), dataIndex: 'depth', width: 82, render: (v: number) => v.toFixed(2) },
    { title: t('倾角'), dataIndex: 'dip', width: 54, render: (v: number) => `${v.toFixed(0)}°` },
    { title: t('倾向'), dataIndex: 'azimuth', width: 54, render: (v: number | null) => (v == null ? '—' : `${v.toFixed(0)}°`) },
    { title: t('覆盖'), dataIndex: 'coverage', width: 58, render: (v: number) => <span style={{ whiteSpace: 'nowrap' }}>{Math.round(v * 100)}%</span> },
    {
      title: t('类型'), dataIndex: 'type', width: 96,
      render: (v: ImageLogFracture['type'], row: ImageLogFracture) => (
        <Select
          size="small" value={v} style={{ width: 88 }}
          options={FRACTURE_TYPES.map((o) => ({ value: o.value, label: t(o.label) }))}
          onClick={(e) => e.stopPropagation()}
          onChange={(type) => review({ fractures: [{ id: row.id, type }] })}
        />
      ),
    },
    {
      title: t('复核'), dataIndex: 'status', width: 92,
      render: (v: ImageLogPickStatus, row: ImageLogFracture) => (
        <span style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button type="button" title={t('确认')} onClick={() => setFractureStatus([row.id], v === 'accepted' ? 'candidate' : 'accepted')}
            style={pickButton(v === 'accepted', STATUS_COLOR.accepted)}>✓</button>
          <button type="button" title={t('否决')} onClick={() => setFractureStatus([row.id], v === 'rejected' ? 'candidate' : 'rejected')}
            style={pickButton(v === 'rejected', STATUS_COLOR.rejected)}>✕</button>
        </span>
      ),
    },
  ];

  const featureColumns = [
    { title: t('类型'), dataIndex: 'type', width: 70, render: (v: ImageLogBoreholeFeature['type']) => (
      <Tag color={v === 'breakout' ? 'volcano' : 'blue'} style={{ marginRight: 0 }}>{v === 'breakout' ? t('崩落') : t('诱导缝')}</Tag>) },
    { title: t('井段 (m)'), key: 'range', width: 110, render: (_: unknown, row: ImageLogBoreholeFeature) => `${row.top_depth.toFixed(1)}–${row.bottom_depth.toFixed(1)}` },
    { title: t('方位'), dataIndex: 'azimuth', width: 50, render: (v: number) => `${v.toFixed(0)}°` },
    { title: t('宽度'), dataIndex: 'width', width: 50, render: (v: number) => `${v.toFixed(0)}°` },
    { title: 'SHmax', dataIndex: 'shmax_azimuth', width: 56, render: (v: number) => `${v.toFixed(0)}°` },
    {
      title: t('复核'), dataIndex: 'status', width: 70,
      render: (v: ImageLogPickStatus, row: ImageLogBoreholeFeature) => (
        <span style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button type="button" title={t('确认')} onClick={() => review({ borehole_features: [{ id: row.id, status: v === 'accepted' ? 'candidate' : 'accepted' }] })}
            style={pickButton(v === 'accepted', STATUS_COLOR.accepted)}>✓</button>
          <button type="button" title={t('否决')} onClick={() => review({ borehole_features: [{ id: row.id, status: v === 'rejected' ? 'candidate' : 'rejected' }] })}
            style={pickButton(v === 'rejected', STATUS_COLOR.rejected)}>✕</button>
        </span>
      ),
    },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#FFFFFF' }}>
      <header style={{ padding: '12px 20px', borderBottom: `1px solid ${TH.border}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: TH.text }}>{t('成像测井解释')}</div>
        <div style={{ fontSize: 12, color: TH.textMuted, marginTop: 2 }}>
          {t('上传成像测井 PDF 或图片, 自动拾取裂缝正弦与井壁崩落; 结果为候选, 人工确认后才发布到天然裂缝表')}
        </div>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '290px minmax(0,1fr) 470px' }}>
        {/* 左: 上传与参数 */}
        <aside style={{ borderRight: `1px solid ${TH.border}`, overflowY: 'auto', padding: 14, background: '#FAFBFC' }}>
          <Upload.Dragger
            accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.bmp"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(f) => { setFile(f); return false; }}
            height={128}
          >
            <p style={{ margin: 0 }}><InboxOutlined style={{ fontSize: 26, color: TH.textMuted }} /></p>
            <p style={{ fontSize: 12, margin: '4px 0 0', color: TH.textSec }}>
              {file ? file.name : t('点击或拖入成像测井 PDF / 图片')}
            </p>
            <p style={{ fontSize: 10.5, margin: '2px 0 0', color: TH.textMuted }}>{t('图像需为 0–360° 展开图, 左边缘为起始方位')}</p>
          </Upload.Dragger>
          <div style={{ height: 4 }} />

          <div style={sectionTitle}>{t('深度与井眼')}</div>
          {numberField('顶深 (m)', 'top_depth', { min: 0, step: 0.1 })}
          {numberField('底深 (m)', 'bottom_depth', { min: 0, step: 0.1 })}
          {numberField('井径 (mm)', 'diameter_mm', { min: 50, max: 1000, step: 1 })}
          {numberField('起始方位 (°)', 'start_azimuth', { min: 0, max: 359, step: 1 })}
          <label style={fieldStyle}>
            <span>{t('PDF 页码')}</span>
            <InputNumber size="small" style={{ width: 110 }} min={1} value={params.page + 1}
              onChange={(v) => setParams((p) => ({ ...p, page: Math.max(0, Number(v ?? 1) - 1) }))} />
          </label>

          <div style={sectionTitle}>{t('图像区域 (去掉表头/刻度道)')}</div>
          {(['crop_left', 'crop_right', 'crop_top', 'crop_bottom'] as const).map((key) => (
            <label key={key} style={fieldStyle}>
              <span>{t({ crop_left: '左边界 %', crop_right: '右边界 %', crop_top: '上边界 %', crop_bottom: '下边界 %' }[key])}</span>
              <InputNumber size="small" style={{ width: 110 }} min={0} max={100} step={1}
                value={Math.round(params[key] * 1000) / 10}
                onChange={(v) => setParams((p) => ({ ...p, [key]: Math.min(1, Math.max(0, Number(v ?? 0) / 100)) }))} />
            </label>
          ))}

          <div style={sectionTitle}>{t('识别参数')}</div>
          <label style={fieldStyle}>
            <span>{t('特征颜色')}</span>
            <Select size="small" style={{ width: 110 }} value={params.dark_features ? 'dark' : 'bright'}
              onChange={(v) => setParams((p) => ({ ...p, dark_features: v === 'dark' }))}
              options={[{ value: 'dark', label: t('暗色 (电成像)') }, { value: 'bright', label: t('亮色') }]} />
          </label>
          {numberField('最大倾角 (°)', 'max_dip', { min: 10, max: 89, step: 1 })}
          <div style={{ fontSize: 11.5, color: TH.textSec, marginTop: 6 }}>
            {t('最小方位覆盖率')} {Math.round(params.min_coverage * 100)}%
          </div>
          <Slider min={10} max={100} value={Math.round(params.min_coverage * 100)}
            onChange={(v) => setParams((p) => ({ ...p, min_coverage: v / 100 }))} />

          <Button type="primary" block loading={busy} disabled={!file} onClick={() => void runUpload()}
            style={{ background: file ? TH.accent : undefined, marginTop: 4 }}>
            {t('上传并识别')}
          </Button>
          <Button block loading={busy} disabled={!record} onClick={() => void runReanalyze()} style={{ marginTop: 8 }}>
            {t('按当前参数重新识别')}
          </Button>
          {record && <div style={{ fontSize: 10.5, color: TH.textMuted, marginTop: 4 }}>{t('重新识别会清空当前的人工复核状态')}</div>}

          <div style={sectionTitle}>{t('识别记录')}</div>
          {records.length === 0 && <div style={{ fontSize: 11.5, color: TH.textMuted }}>{t('暂无记录')}</div>}
          {records.map((r) => (
            <div key={r.id} onClick={() => void openRecord(r.id)} style={{
              padding: '6px 8px', marginBottom: 6, borderRadius: 4, cursor: 'pointer', background: '#fff',
              border: `1px solid ${record?.id === r.id ? TH.accent : TH.soft}`,
              display: 'flex', alignItems: 'flex-start', gap: 6,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: TH.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: TH.textMuted }}>
                  {t('{n} 条裂缝 · {m} 段崩落', { n: r.summary?.fracture_count ?? 0, m: r.summary?.breakout_count ?? 0 })}
                  {r.published_at && <span style={{ color: STATUS_COLOR.accepted }}> · {t('已发布')}</span>}
                </div>
              </div>
              <Popconfirm title={t('删除这条识别记录?')} onConfirm={() => void remove(r.id)} okText={t('删除')} cancelText={t('取消')}>
                <button type="button" onClick={(e) => e.stopPropagation()} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: TH.textMuted }}>×</button>
              </Popconfirm>
            </div>
          ))}
        </aside>

        {/* 中: 图像 + 叠加 */}
        <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0, background: TH.panel }}>
          <div style={{ padding: '8px 12px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: `1px solid ${TH.border}`, background: '#fff' }}>
            <Segmented size="small" value={layer} onChange={(v) => setLayer(v as Layer)} options={[
              { value: 'work', label: t('原图') }, { value: 'clahe', label: t('增强') },
              { value: 'binary', label: t('二值化') }, { value: 'edges', label: t('边缘') },
            ]} />
            <Segmented size="small" value={zoom} onChange={(v) => setZoom(Number(v))} options={[
              { value: 0.25, label: '0.25×' }, { value: 0.5, label: '0.5×' }, { value: 1, label: '1×' },
            ]} />
            <span style={{ fontSize: 11, color: TH.textMuted, marginLeft: 'auto' }}>
              <span style={{ color: STATUS_COLOR.candidate }}>━</span> {t('候选')}{' '}
              <span style={{ color: STATUS_COLOR.accepted }}>━</span> {t('已确认')}{' '}
              <span style={{ color: STATUS_COLOR.rejected }}>┅</span> {t('已否决')}{' '}
              <span style={{ color: '#E74C3C' }}>▮</span> {t('崩落')}{' '}
              <span style={{ color: '#3498DB' }}>▮</span> {t('诱导缝')}
            </span>
          </div>
          <div ref={viewerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 24px' }}>
            {busy && !record && <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>}
            {!record && !busy && <Empty style={{ marginTop: 80 }} description={t('上传成像测井后在这里显示识别结果')} />}
            {record && geometry && (
              <div style={{ display: 'flex', gap: 6 }}>
                <svg width={56} height={geometry.height} style={{ flexShrink: 0 }}>
                  {geometry.ticks.map((d) => (
                    <g key={d}>
                      <line x1={48} x2={56} y1={geometry.depthToY(d)} y2={geometry.depthToY(d)} stroke={TH.textMuted} />
                      <text x={44} y={geometry.depthToY(d) + 3} fontSize={10} textAnchor="end" fill={TH.textSec}>{d}</text>
                    </g>
                  ))}
                </svg>
                <div style={{ position: 'relative', width: VIEW_WIDTH, height: geometry.height, flexShrink: 0, background: '#fff', boxShadow: '0 0 0 1px #D6DAE0' }}>
                  {images ? (
                    <img src={images[layer]} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated' }} />
                  ) : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>}
                  <svg width={VIEW_WIDTH} height={geometry.height} style={{ position: 'absolute', inset: 0 }}>
                    {record.borehole_features.filter((f) => f.status !== 'rejected').map((f) => {
                      const color = f.type === 'breakout' ? '#E74C3C' : '#3498DB';
                      const y = f.top_px * geometry.scaleY;
                      const h = Math.max(2, (f.bottom_px - f.top_px) * geometry.scaleY);
                      const azToX = (az: number) => ((((az - record.params.start_azimuth) % 360) + 360) % 360) / 360 * VIEW_WIDTH;
                      const w = Math.max(2, (f.width / 360) * VIEW_WIDTH);
                      return [f.azimuth, f.azimuth + 180].map((az) => (
                        <rect key={`${f.id}-${az}`} x={azToX(az) - w / 2} y={y} width={w} height={h}
                          fill={color} fillOpacity={f.status === 'accepted' ? 0.35 : 0.18} stroke={color} strokeWidth={1}
                          onClick={() => setSelected(null)} />
                      ));
                    })}
                    {record.fractures.map((f) => (
                      <polyline key={f.id} points={geometry.curve(f)} fill="none"
                        stroke={STATUS_COLOR[f.status]} strokeWidth={selected === f.id ? 3.5 : 2}
                        strokeDasharray={f.status === 'rejected' ? '4 4' : undefined}
                        style={{ cursor: 'pointer' }} onClick={() => setSelected(f.id)} />
                    ))}
                  </svg>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 右: 复核与发布 */}
        <aside style={{ borderLeft: `1px solid ${TH.border}`, overflowY: 'auto', padding: 14 }}>
          {!record ? <div style={{ fontSize: 12, color: TH.textMuted }}>{t('暂无识别结果')}</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 }}>
                {[
                  { label: '裂缝候选', value: record.fractures.length },
                  { label: '已确认', value: acceptedCount },
                  { label: '崩落段长 (m)', value: record.summary.breakout_length_m },
                  { label: '崩落段', value: record.summary.breakout_count },
                  { label: '诱导缝段', value: record.summary.induced_fracture_count },
                  { label: 'SHmax 方位', value: record.summary.shmax_azimuth == null ? '—' : `${record.summary.shmax_azimuth}°` },
                ].map((s) => (
                  <div key={s.label} style={{ border: `1px solid ${TH.soft}`, borderRadius: 6, padding: '6px 8px' }}>
                    <div style={{ fontSize: 10.5, color: TH.textMuted }}>{t(s.label)}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: TH.text }}>{s.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <b style={{ fontSize: 12.5, color: TH.text }}>{t('裂缝拾取')}</b>
                <span style={{ flex: 1 }} />
                <Button size="small" onClick={() => void setFractureStatus(record.fractures.filter((f) => f.status === 'candidate').map((f) => f.id), 'accepted')}
                  disabled={!record.fractures.some((f) => f.status === 'candidate')}>{t('候选全部确认')}</Button>
                <Button size="small" onClick={() => void setFractureStatus(record.fractures.map((f) => f.id), 'candidate')}>{t('重置')}</Button>
              </div>
              <Table
                size="small" rowKey="id" pagination={false} columns={fractureColumns} dataSource={record.fractures}
                scroll={{ y: 300 }}
                locale={{ emptyText: t('没有识别到裂缝, 可降低最小方位覆盖率或调整图像区域') }}
                onRow={(row) => ({
                  onClick: () => focus(row.id, row.y0_px),
                  style: { cursor: 'pointer', background: selected === row.id ? '#FFF6E5' : undefined, opacity: row.status === 'rejected' ? 0.5 : 1 },
                })}
              />

              <div style={{ margin: '14px 0 6px' }}><b style={{ fontSize: 12.5, color: TH.text }}>{t('井壁崩落 / 钻井诱导缝')}</b></div>
              <Table
                size="small" rowKey="id" pagination={false} columns={featureColumns} dataSource={record.borehole_features}
                locale={{ emptyText: t('未识别到成对的崩落或诱导缝') }}
                onRow={(row) => ({ onClick: () => focus(-row.id, row.top_px), style: { cursor: 'pointer' } })}
              />
              <div style={{ fontSize: 10.5, color: TH.textMuted, marginTop: 6, lineHeight: 1.6 }}>
                {t('崩落沿最小水平主应力方向展布, 诱导缝沿最大水平主应力方向; SHmax 可用于校核地应力方向与坍塌压力模型。')}
              </div>

              <div style={{ marginTop: 14, padding: 10, border: `1px solid ${TH.soft}`, borderRadius: 6, background: '#FAFBFC' }}>
                <Button type="primary" block disabled={!acceptedCount} onClick={() => void publish()} style={{ background: acceptedCount ? TH.accent : undefined }}>
                  {t('发布已确认裂缝到天然裂缝表 ({n} 条)', { n: acceptedCount })}
                </Button>
                <div style={{ fontSize: 10.5, color: TH.textMuted, marginTop: 6 }}>
                  {record.published_at
                    ? t('上次发布 {n} 条; 再次发布会替换上一次的结果', { n: record.published_fracture_ids.length })
                    : t('只发布已确认的裂缝, 来源记为「成像测井」')}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  fontSize: 11.5, color: '#4F6680', marginBottom: 6,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11.5, fontWeight: 700, color: '#1E2A3A', margin: '14px 0 8px',
};

const pickButton = (active: boolean, color: string): React.CSSProperties => ({
  width: 26, height: 22, borderRadius: 4, cursor: 'pointer', fontSize: 12, lineHeight: 1,
  border: `1px solid ${active ? color : '#D6DAE0'}`, background: active ? color : '#fff', color: active ? '#fff' : '#4F6680',
});
